// Weapon runtime: one per equipped weapon instance (created by the player module).
//
// Effective parameters = WeaponDef + installed mods (params.ts) + player stats (fireRate,
// heatCapacity, cooling); outgoing damage multipliers are applied later by the damage system.
// Heat follows heat.ts. Six fire behaviours: auto, spread, beam_chain, charge, missile_burst,
// lobber. Every shot group emits weapon:fired, plays the weapon sound, flashes the muzzle,
// sets `kick` (read by MechView) and shakes the camera.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, GameContext, LoopHandle, ProjectileSpec, WeaponRuntimeApi } from '../../core/contracts';
import type { WeaponDef, WeaponInstance, WeaponModifier } from '../../core/types';
import { modModifier, weaponDef } from './data';
import { angleDiff } from './geom';
import {
  HEAT_EV_OVERHEAT,
  HEAT_EV_VENT_END,
  HEAT_EV_WARNING,
  addHeat,
  createHeatState,
  tickHeat,
  ventHeat,
} from './heat';
import { createSafeFx, destroyObj, enemyList, flag, livePlayer, playerStats, type ChainOptsEx, type ProjectileSpecEx } from './internal';
import { computeWeaponParams, fanOffset, mergeModifiers, type WeaponParams } from './params';
import { createSfx, jitterPitch } from './sfx';
import { CTEX, ensureCombatTextures } from './textures';

/** Beam weapons lock onto targets within this half-angle of the aim. */
const BEAM_CONE = (28 * Math.PI) / 180;
/** MELTDOWN: shots fired while overheated. */
const MELTDOWN_DAMAGE = 1.5;
const MELTDOWN_HP_COST = 2;
/** Charge weapons: a tap still costs at least this fraction of a full second of charge heat. */
const CHARGE_MIN_HEAT = 0.35;
/** Point-blank backtrace for player shots (shoulder → muzzle). */
const BACKTRACE = 46;
/** Delay after drawing a weapon before it can fire. */
const DRAW_DELAY = 0.12;

export function createWeaponRuntime(ctx: GameContext, inst: WeaponInstance): WeaponRuntimeApi {
  const def: WeaponDef = weaponDef(inst.defId);
  const fx = createSafeFx(ctx);
  const sfx = createSfx(ctx);
  const heat = createHeatState();

  let params: WeaponParams = computeWeaponParams(def, mergeModifiers([]), { fireRate: 1, heatCapacity: 1, cooling: 1 });
  // Cache key for params: stats it depends on + installed mod uids.
  let keyFire = NaN;
  let keyCap = NaN;
  let keyCool = NaN;
  let keyMods: string[] = [];

  let cooldown = 0;
  let kick = 0;
  let wasTrigger = false;
  let destroyed = false;
  const muzzle = { x: 0, y: 0, angle: 0 };

  // Bursts (missile salvos and burst-fire mods).
  let burstLeft = 0;
  let burstTotal = 0;
  let burstT = 0;
  let burstInterval = 0.06;

  // Charge.
  let charging = false;
  let charge = 0;
  let chargeHeat = 0;
  let chargeFull = false;
  let chargeLoop: LoopHandle | null = null;
  let chargeGlow: Phaser.GameObjects.Image | null = null;
  let chargeSparkT = 0;

  // Beam.
  let beamLoop: LoopHandle | null = null;

  // Overheat lockout puffs.
  let ventPuffT = 0;

  function refreshParams(): void {
    const st = playerStats(ctx);
    const mods = inst.mods ?? [];
    let same = st.fireRate === keyFire && st.heatCapacity === keyCap && st.cooling === keyCool && mods.length === keyMods.length;
    if (same) for (let i = 0; i < mods.length; i++) if (mods[i].uid !== keyMods[i]) same = false;
    if (same) return;
    keyFire = st.fireRate;
    keyCap = st.heatCapacity;
    keyCool = st.cooling;
    keyMods = mods.map((m) => m.uid);
    const modifiers: (WeaponModifier | null)[] = mods.map((m) => modModifier(m.defId));
    params = computeWeaponParams(def, mergeModifiers(modifiers), st);
  }

  const meltdown = (): boolean => flag(ctx, 'MELTDOWN');

  function handleHeatEvents(ev: number): void {
    if (ev & HEAT_EV_WARNING) sfx.play('heat_warning', { volume: 0.7 });
    if (ev & HEAT_EV_OVERHEAT) {
      ctx.bus.emit('player:overheat', { weapon: def.id });
      fx.vent(muzzle.x, muzzle.y, muzzle.angle);
      sfx.play('overheat', { volume: 0.9, x: muzzle.x });
      ventPuffT = 0.15;
      if (!meltdown()) {
        cancelCharge(true);
        burstLeft = 0;
        stopBeam();
      } else {
        fx.floatText(muzzle.x, muzzle.y - 30, 'MELTDOWN', { color: COLORS.threatHot, size: 18, duration: 600 });
      }
    }
    if (ev & HEAT_EV_VENT_END) {
      ctx.bus.emit('player:ventEnd', { weapon: def.id });
      sfx.play('vent', { volume: 0.45 });
    }
  }

  function heatUp(amount: number): void {
    handleHeatEvents(addHeat(heat, amount, params.overheatTime));
  }

  /** MELTDOWN shots while overheated: +50% damage, 2 HP each (never lethal). */
  function meltdownShot(): number {
    if (!heat.overheated || !meltdown()) return 1;
    const p = livePlayer(ctx);
    if (p && p.alive && p.hp > 1) {
      const cost = Math.min(MELTDOWN_HP_COST, p.hp - 1);
      p.hp -= cost;
      ctx.run.stats.damageTaken += cost;
    }
    return MELTDOWN_DAMAGE;
  }

  function baseSpec(angle: number, damage: number): ProjectileSpecEx {
    const P = params;
    const speed = Math.max(60, P.projectileSpeed);
    return {
      owner: 'player',
      x: muzzle.x,
      y: muzzle.y,
      angle,
      speed,
      damage,
      tags: P.tags,
      texture: def.projectile.texture,
      scale: def.projectile.scale,
      tint: def.projectile.tint,
      glow: def.behavior !== 'spread' && def.behavior !== 'missile_burst',
      radius: def.projectile.radius,
      lifetime: P.range / speed,
      pierce: P.pierce,
      knockback: P.knockback,
      statuses: P.statuses.length > 0 ? P.statuses : undefined,
      explodeOnKill: P.explodeOnKill ? { radius: P.explodeOnKill.radius, damage: P.explodeOnKill.damageMult } : undefined,
      weaponId: def.id,
      trail: 'pulse',
      backtrace: BACKTRACE,
    };
  }

  /** Shared per-shot-group feedback + event. */
  function fired(specs: ProjectileSpec[], opts: { flash?: number; shake?: number; shakeMs?: number; kick?: number; volume?: number; pitch?: number }): void {
    ctx.bus.emit('weapon:fired', { weapon: def.id, x: muzzle.x, y: muzzle.y, angle: muzzle.angle, specs, secondary: false });
    sfx.play(def.sfx, { volume: opts.volume ?? 0.8, pitch: opts.pitch ?? jitterPitch(0.05), x: muzzle.x });
    fx.muzzleFlash(muzzle.x, muzzle.y, muzzle.angle, def.muzzleColor, opts.flash ?? 1);
    kick = Math.min(1, Math.max(kick, opts.kick ?? def.recoil));
    const sh = opts.shake ?? def.shake;
    if (sh > 0) fx.shake(sh, opts.shakeMs ?? 70);
  }

  // ───────────── behaviours ─────────────

  /** One round of an auto / spread weapon (bursts call this per round). */
  function fireRound(): void {
    const P = params;
    const mult = meltdownShot();
    const specs: ProjectileSpec[] = [];
    if (def.behavior === 'spread') {
      const n = P.pellets + P.extraProjectiles * 2;
      const cone = P.spread;
      for (let i = 0; i < n; i++) {
        const slot = n > 1 ? -cone / 2 + (cone * (i + 0.5)) / n : 0;
        const a = muzzle.angle + slot + ctx.rng.float(-0.4, 0.4) * (cone / Math.max(1, n));
        const s = baseSpec(a, P.damage * mult);
        s.speed *= ctx.rng.float(0.9, 1.08);
        s.lifetime = P.range / s.speed;
        s.falloff = { start: P.range * 0.35, end: P.range, min: 0.35 };
        s.trail = 'pulse';
        s.impact = 'pellet';
        specs.push(ctx.projectiles.spawn(s).spec);
      }
      heatUp(P.heatPerShot);
      fired(specs, { flash: 1.5, shake: def.shake, shakeMs: 150, kick: def.recoil, volume: 0.9 });
      return;
    }
    // auto
    const n = 1 + P.extraProjectiles;
    const step = n > 1 ? Math.min(0.35, Math.max(0.06, P.spread / Math.max(1, P.extraProjectiles))) : 0;
    const jitterCone = n > 1 ? def.spread : P.spread;
    const base = muzzle.angle + ctx.rng.float(-0.5, 0.5) * jitterCone;
    for (let i = 0; i < n; i++) {
      const s = baseSpec(base + fanOffset(i, step), P.damage * mult);
      specs.push(ctx.projectiles.spawn(s).spec);
    }
    heatUp(P.heatPerShot);
    fired(specs, { flash: 1, shakeMs: 50 });
  }

  /** One micro-missile of a salvo. */
  function fireMissile(index: number, count: number): void {
    const P = params;
    const mult = index === 0 ? meltdownShot() : heat.overheated && meltdown() ? MELTDOWN_DAMAGE : 1;
    // Alternate outer → inner across the fan so the salvo spreads symmetrically.
    const order = index % 2 === 0 ? index / 2 : count - 1 - (index - 1) / 2;
    const frac = count > 1 ? (order / (count - 1)) * 2 - 1 : 0;
    const s = baseSpec(muzzle.angle + frac * (Math.max(0.2, P.spread) / 2), P.damage * mult);
    s.launchSpeedFrac = 0.45;
    s.lifetime = (P.range / Math.max(60, P.projectileSpeed)) * 1.35;
    s.homing = def.homing ? { ...def.homing } : { turnRate: 6, range: 600, delay: 0.12 };
    s.explode = def.explode ? { radius: def.explode.radius, damage: P.damage * mult * def.explode.damageMult } : { radius: 60, damage: P.damage * 0.5 };
    s.trail = 'rocket';
    s.backtrace = 0;
    const spec = ctx.projectiles.spawn(s).spec;
    fired([spec], { flash: 0.8, shakeMs: 60, volume: 0.6, pitch: jitterPitch(0.08) * (1 + index * 0.03) });
  }

  function fireLobber(): void {
    const P = params;
    const mult = meltdownShot();
    const specs: ProjectileSpec[] = [];
    const n = 1 + P.extraProjectiles;
    for (let i = 0; i < n; i++) {
      const s = baseSpec(muzzle.angle + fanOffset(i, 0.14), P.damage * mult);
      s.gravity = def.gravity ?? 1500;
      s.lifetime = Math.max(0.6, Math.min(2.6, (P.range / Math.max(60, P.projectileSpeed)) * 1.3));
      s.pierce = 0;
      s.trail = 'gravity';
      s.backtrace = 20;
      if (def.explode) s.explode = { radius: def.explode.radius, damage: P.damage * mult * def.explode.damageMult, tags: ['gravity', 'explosive'] };
      const f = def.field ?? { radius: 160, duration: 1.6, strength: 1500, implodeDamageMult: 1.2 };
      s.field = { radius: f.radius, duration: f.duration, strength: f.strength, implodeDamage: P.damage * mult * f.implodeDamageMult };
      specs.push(ctx.projectiles.spawn(s).spec);
    }
    heatUp(P.heatPerShot);
    fired(specs, { flash: 1.2, shakeMs: 110 });
  }

  /** Nearest enemy in the beam cone with line of sight. */
  function beamTarget(): EnemyApi | null {
    const range = params.range;
    const cone = Math.max(BEAM_CONE, def.spread / 2);
    let best: EnemyApi | null = null;
    let bestD = Infinity;
    let bestProp: EnemyApi | null = null;
    let bestPropD = Infinity;
    for (const e of enemyList(ctx)) {
      if (!e.alive || e.spawning) continue;
      const dx = e.x - muzzle.x;
      const dy = e.y - muzzle.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const edge = dist - e.radius;
      if (edge > range) continue;
      // Angular test against the target's body: a wide target near the edge of the cone still counts.
      const half = dist > 1 ? Math.atan(e.radius / dist) : Math.PI;
      if (angleDiff(Math.atan2(dy, dx), muzzle.angle) > cone + half) continue;
      const isProp = e.kind === 'prop';
      if (isProp ? edge >= bestPropD : edge >= bestD) continue;
      if (!lineOfSight(e)) continue;
      if (isProp) {
        bestProp = e;
        bestPropD = edge;
      } else {
        best = e;
        bestD = edge;
      }
    }
    return best ?? bestProp;
  }

  function lineOfSight(e: EnemyApi): boolean {
    try {
      const r = ctx.world.raycast(muzzle.x, muzzle.y, e.x, e.y);
      if (!r.hit) return true;
      return r.dist >= Math.hypot(e.x - muzzle.x, e.y - muzzle.y) - e.radius;
    } catch {
      return true;
    }
  }

  function beamTick(): void {
    const P = params;
    const mult = meltdownShot();
    const target = beamTarget();
    const color = def.muzzleColor;
    if (target) {
      const chain = def.chain ?? { count: 2, range: 220, falloff: 0.8 };
      const opts: ChainOptsEx = {
        count: Math.max(0, chain.count + Math.round(playerStats(ctx).chainBonus) + P.extraProjectiles),
        range: chain.range,
        damage: P.damage * mult,
        falloff: chain.falloff,
        tags: P.tags,
        statuses: P.statuses.length > 0 ? P.statuses : undefined,
        weaponId: def.id,
        color,
        width: 4,
        label: def.id,
      };
      ctx.damage.chainLightning(muzzle, target, opts);
    } else {
      // Nothing to lock onto: a short crackling arc into the air (still builds heat).
      const len = P.range * (0.3 + Math.random() * 0.15);
      let ex = muzzle.x + Math.cos(muzzle.angle) * len;
      let ey = muzzle.y + Math.sin(muzzle.angle) * len;
      try {
        const r = ctx.world.raycast(muzzle.x, muzzle.y, ex, ey);
        if (r.hit) {
          ex = r.x;
          ey = r.y;
          fx.sparks(ex, ey, muzzle.angle + Math.PI, { count: 3, color, speed: 180, spread: 1.4, life: 140 });
          // Nudge into the wall so the arc damages the block it touches.
          ctx.world.damageTerrain(ex + Math.cos(muzzle.angle) * 2, ey + Math.sin(muzzle.angle) * 2, P.damage * mult);
        }
      } catch {
        // no world: free arc
      }
      const mx = (muzzle.x + ex) / 2 + (Math.random() - 0.5) * 30;
      const my = (muzzle.y + ey) / 2 + (Math.random() - 0.5) * 30;
      fx.lightning(
        [
          { x: muzzle.x, y: muzzle.y },
          { x: mx, y: my },
          { x: ex, y: ey },
        ],
        { color, width: 2, duration: 80, jitter: 14 },
      );
    }
    heatUp(P.heatPerShot);
    ctx.bus.emit('weapon:fired', { weapon: def.id, x: muzzle.x, y: muzzle.y, angle: muzzle.angle, specs: [], secondary: false });
    sfx.play(def.sfx, { volume: target ? 0.5 : 0.32, pitch: jitterPitch(0.12), x: muzzle.x }, 45);
    fx.muzzleFlash(muzzle.x, muzzle.y, muzzle.angle, color, 0.55);
    kick = Math.max(kick, def.recoil);
    if (def.shake > 0 && target) fx.shake(def.shake, 40);
  }

  function startBeam(): void {
    if (!beamLoop) beamLoop = sfx.loop('beam', { volume: 0.5 });
  }

  function stopBeam(): void {
    if (beamLoop) {
      try {
        beamLoop.stop(120);
      } catch {
        // audio gone
      }
      beamLoop = null;
    }
  }

  function ensureGlow(): Phaser.GameObjects.Image | null {
    if (chargeGlow || destroyed) return chargeGlow;
    try {
      ensureCombatTextures(ctx.scene);
      const key = ctx.scene.textures.exists(TEX.soft) ? TEX.soft : CTEX.soft;
      chargeGlow = ctx.scene.add.image(muzzle.x, muzzle.y, key).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.FX_ADD).setTint(def.muzzleColor).setVisible(false);
    } catch {
      chargeGlow = null;
    }
    return chargeGlow;
  }

  function cancelCharge(fizzle: boolean): void {
    if (!charging && !chargeLoop) return;
    if (fizzle && charging) fx.sparks(muzzle.x, muzzle.y, muzzle.angle, { count: 6, color: def.muzzleColor, speed: 160, spread: 2, life: 200 });
    charging = false;
    charge = 0;
    chargeHeat = 0;
    chargeFull = false;
    if (chargeLoop) {
      try {
        chargeLoop.stop(90);
      } catch {
        // audio gone
      }
      chargeLoop = null;
    }
    chargeGlow?.setVisible(false);
  }

  function updateCharge(dt: number, trigger: boolean): void {
    const P = params;
    if (trigger && !charging && cooldown <= 0 && !wasTrigger) startCharge();
    else if (trigger && !charging && cooldown <= 0 && wasTrigger) startCharge();
    if (charging) {
      if (trigger) {
        charge = Math.min(1, charge + dt / P.chargeTime);
        const h = P.heatPerShot * dt;
        chargeHeat += h;
        heatUp(h);
        if (!charging) return; // overheated while charging
        chargeLoop?.setPitch(0.75 + charge * 0.75);
        chargeLoop?.setVolume(0.35 + charge * 0.35);
        if (charge >= 1 && !chargeFull) {
          chargeFull = true;
          sfx.play('rail_charge', { volume: 0.7, pitch: 1.4, x: muzzle.x });
          fx.ring(muzzle.x, muzzle.y, 10, { color: def.muzzleColor, duration: 180, width: 3, fromRadius: 44 });
        }
        chargeSparkT -= dt;
        if (chargeSparkT <= 0) {
          chargeSparkT = chargeFull ? 0.05 : 0.09;
          fx.sparks(muzzle.x, muzzle.y, Math.random() * Math.PI * 2, { count: chargeFull ? 2 : 1, color: def.muzzleColor, speed: 70 + charge * 90, spread: 0.4, life: 140 });
        }
      } else {
        releaseCharge();
      }
    }
    const g = chargeGlow;
    if (g) {
      if (charging) {
        const flick = 0.85 + Math.random() * 0.15;
        const size = (12 + 46 * charge) * (chargeFull ? 1 + 0.12 * Math.sin(performance.now() / 45) : 1);
        g.setVisible(true).setPosition(muzzle.x, muzzle.y).setDisplaySize(size, size).setAlpha((0.35 + 0.55 * charge) * flick);
      } else g.setVisible(false);
    }
  }

  function startCharge(): void {
    charging = true;
    charge = 0;
    chargeHeat = 0;
    chargeFull = false;
    chargeSparkT = 0;
    ensureGlow();
    if (!chargeLoop) chargeLoop = sfx.loop('charge_hum', { volume: 0.35, pitch: 0.75 });
  }

  function releaseCharge(): void {
    const P = params;
    const c = charge;
    const full = c >= 1;
    // Taps still cost heat so spamming weak shots is never better than charging.
    const minHeat = P.heatPerShot * CHARGE_MIN_HEAT;
    if (chargeHeat < minHeat) heatUp(minHeat - chargeHeat);
    cancelCharge(false);
    const mult = meltdownShot();
    const s = baseSpec(muzzle.angle, P.damage * (0.35 + 0.65 * c) * mult);
    s.pierce = P.pierce + (full ? 3 : 0);
    s.speed = Math.max(60, P.projectileSpeed) * (0.75 + 0.25 * c);
    s.lifetime = P.range / s.speed;
    s.radius = def.projectile.radius * (0.7 + 0.5 * c);
    s.scale = (def.projectile.scale ?? 1) * (0.65 + 0.55 * c);
    s.knockback = P.knockback * (0.4 + 0.6 * c);
    s.trail = 'rail';
    s.impact = c > 0.6 ? 'heavy' : 'bullet';
    s.label = full ? 'rail_full' : 'rail';
    const spec = ctx.projectiles.spawn(s).spec;
    fired([spec], {
      flash: 0.8 + 1.4 * c,
      shake: def.shake * (0.3 + 0.7 * c),
      shakeMs: 110 + 90 * c,
      kick: def.recoil * (0.35 + 0.65 * c),
      volume: 0.55 + 0.4 * c,
      pitch: full ? 0.92 : 1.1 - 0.15 * c,
    });
    if (full) {
      ctx.time.hitstop(35);
      fx.ring(muzzle.x, muzzle.y, 70, { color: def.muzzleColor, duration: 200, width: 4, fromRadius: 12 });
    }
    cooldown = Math.max(cooldown, 0) + P.interval;
  }

  /** Start a trigger pull for the projectile behaviours. */
  function pull(): void {
    const P = params;
    switch (def.behavior) {
      case 'auto':
      case 'spread':
        if (P.burst) {
          burstTotal = burstLeft = P.burst.count;
          burstInterval = P.burst.interval;
          burstT = 0;
        } else fireRound();
        break;
      case 'missile_burst': {
        const count = (P.burst?.count ?? 3) + P.extraProjectiles;
        burstTotal = burstLeft = count;
        burstInterval = P.burst?.interval ?? 0.08;
        burstT = 0;
        heatUp(P.heatPerShot);
        break;
      }
      case 'lobber':
        fireLobber();
        break;
      default:
        break;
    }
  }

  function advanceBurst(dt: number): void {
    if (burstLeft <= 0) return;
    burstT -= dt;
    while (burstT <= 0 && burstLeft > 0) {
      const index = burstTotal - burstLeft;
      burstLeft--;
      if (def.behavior === 'missile_burst') fireMissile(index, burstTotal);
      else fireRound();
      burstT += burstInterval;
      if (heat.overheated && !meltdown()) {
        burstLeft = 0;
        break;
      }
    }
  }

  function lockedFx(dt: number): void {
    ventPuffT -= dt;
    if (ventPuffT <= 0) {
      ventPuffT = 0.2;
      fx.vent(muzzle.x, muzzle.y, muzzle.angle);
    }
  }

  const api: WeaponRuntimeApi = {
    instance: inst,
    def,
    get heat() {
      return heat.heat;
    },
    get overheated() {
      return heat.overheated;
    },
    get overheatRemaining() {
      return heat.overheatRemaining;
    },
    get charge() {
      return charge;
    },
    get kick() {
      return kick;
    },

    update(dt, trigger, m) {
      if (destroyed) return;
      muzzle.x = m.x;
      muzzle.y = m.y;
      muzzle.angle = m.angle;
      refreshParams();
      kick = Math.max(0, kick - dt * 5);
      if (cooldown > 0 || def.behavior !== 'charge') cooldown -= dt;
      // Idle triggers keep the "first shot is immediate" promise without banking extra shots.
      if (cooldown < -0.05) cooldown = -0.05;

      handleHeatEvents(tickHeat(heat, dt, params.coolPerSec, params.coolDelay, false));
      const locked = heat.overheated && !meltdown();
      if (locked) {
        cancelCharge(true);
        burstLeft = 0;
        stopBeam();
        lockedFx(dt);
        wasTrigger = trigger;
        return;
      }

      switch (def.behavior) {
        case 'beam_chain':
          if (trigger) {
            startBeam();
            if (cooldown <= 0) {
              beamTick();
              cooldown += params.interval;
            }
          } else stopBeam();
          break;
        case 'charge':
          updateCharge(dt, trigger);
          break;
        default:
          advanceBurst(dt);
          if (trigger && burstLeft <= 0 && cooldown <= 0) {
            pull();
            cooldown += params.interval;
            advanceBurst(0);
          }
          break;
      }
      wasTrigger = trigger;
    },

    updateHolstered(dt) {
      if (destroyed) return;
      kick = Math.max(0, kick - dt * 5);
      if (cooldown > 0) cooldown -= dt;
      cancelCharge(false);
      stopBeam();
      burstLeft = 0;
      handleHeatEvents(tickHeat(heat, dt, params.coolPerSec, params.coolDelay, true));
    },

    onDraw() {
      cooldown = Math.max(cooldown, DRAW_DELAY);
      wasTrigger = false;
    },

    onHolster() {
      cancelCharge(false);
      stopBeam();
      burstLeft = 0;
    },

    vent(amount) {
      handleHeatEvents(ventHeat(heat, amount));
    },

    destroy() {
      if (destroyed) return;
      cancelCharge(false);
      stopBeam();
      destroyed = true;
      destroyObj(chargeGlow);
      chargeGlow = null;
    },
  };
  return api;
}
